// The Branch section of the Store screen.
//
// A store is the company — owner, GST, WhatsApp group, module flags. A branch
// is a place it trades from, and everything that differs between two of those
// places lives here: address, map link, the withstanding amount, the server IP
// and the remote-access credentials.
//
// Two rules the UI has to make obvious, because the API enforces them:
//   * a store always has at least one branch, so the last one cannot be deleted
//   * exactly one branch is the main one, and a store with a single branch
//     always has that branch as main — so the control only appears once there
//     is something to choose between
//
// Used in two shapes:
//   BranchFields  — the bare form. The Add Store screen shows it inline for the
//                   first branch, because there is no store id to POST to yet.
//   BranchPanel   — the list + add/edit/delete/set-main, once the store exists.

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  BG_ALT,
  BLUE,
  BLUE_DARK,
  BLUE_PALE,
  BORDER,
  Cell,
  DANGER,
  FieldGrid,
  INK,
  INK_MUTED,
  INK_SOFT,
  IconField,
  SURFACE,
  Segmented,
  SubSection,
  ToggleRow,
} from "./storeTheme";

// Icons
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DesktopWindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Shape helpers
// ─────────────────────────────────────────────────────────────────────────────

export const emptyBranch = () => ({
  id: null,
  name: "",
  code: "",
  address: "",
  location_url: "",
  amount: "",
  server_ip: "",
  anydesk_id: "",
  ultraview_id: "",
  ultraview_password: "",
  is_main: false,
});

// API row -> form state. Everything is a string in the form so a cleared field
// stays distinguishable from one that was never filled in.
export const branchToForm = (branch) => ({
  id: branch?.id ?? null,
  name: branch?.name || "",
  code: branch?.code || "",
  address: branch?.address || "",
  location_url: branch?.location_url || "",
  amount:
    branch?.amount === null || branch?.amount === undefined
      ? ""
      : String(branch.amount),
  server_ip: branch?.server_ip || "",
  anydesk_id: branch?.anydesk_id || "",
  ultraview_id: branch?.ultraview_id || "",
  ultraview_password: branch?.ultraview_password || "",
  is_main: Boolean(branch?.is_main),
});

// Form state -> API body. An empty amount means "not set", which the backend
// keeps as NULL rather than writing a 0.
export const branchToPayload = (form) => ({
  name: form.name.trim(),
  code: form.code.trim().toUpperCase(),
  address: form.address.trim() || null,
  location_url: form.location_url.trim() || null,
  amount: form.amount === "" ? null : form.amount,
  server_ip: form.server_ip.trim() || null,
  anydesk_id: form.anydesk_id.trim() || null,
  ultraview_id: form.ultraview_id.trim() || null,
  ultraview_password: form.ultraview_password.trim() || null,
});

// Mandatory fields are caught here rather than being left to the API's own
// "x is required" — the field that is wrong has to be the one that lights up.
export const validateBranch = (form) => {
  if (!form.name.trim())
    return { field: "name", message: "Branch name is required" };
  if (!form.code.trim())
    return { field: "code", message: "Branch code is required" };
  return null;
};

// The code is shouted down a phone line and typed into other systems, so it is
// upper-case alphanumerics only (a dash is allowed — migrated codes carry one).
const sanitizeCode = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, "")
    .slice(0, 10);

// decimal(10,2): digits, at most one dot, at most two places after it.
const sanitizeAmount = (value) => {
  const cleaned = String(value || "").replace(/[^0-9.]/g, "");
  const [whole, ...rest] = cleaned.split(".");
  if (rest.length === 0) return whole.slice(0, 8);
  return `${whole.slice(0, 8)}.${rest.join("").slice(0, 2)}`;
};

const formatAmount = (amount) =>
  amount === null || amount === undefined || amount === ""
    ? null
    : Number(amount).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const readRemoteTool = (branch) => {
  if (branch?.anydesk_id) return "anydesk";
  if (branch?.ultraview_id || branch?.ultraview_password) return "ultraviewer";
  return "";
};

const apiMessage = (error, fallback) =>
  error?.response?.data?.statusMessage ||
  error?.statusMessage ||
  error?.message ||
  fallback;

// ─────────────────────────────────────────────────────────────────────────────
// The form
// ─────────────────────────────────────────────────────────────────────────────

// `errorField` is the name of the one field that failed validation, so the save
// attempt paints exactly the box the operator has to go back to.
export const BranchFields = ({ form, onChange, errorField }) => {
  const [remoteTool, setRemoteTool] = useState(() => readRemoteTool(form));

  // Opening the dialog on a different branch has to re-read which tool that
  // branch actually uses, or an AnyDesk branch opens showing UltraViewer.
  useEffect(() => {
    setRemoteTool(readRemoteTool(form));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SubSection label="Branch Details" hint="identity & location">
        <FieldGrid>
          <Cell span={{ xs: 12, sm: 5 }}>
            <IconField
              icon={<HomeWorkRoundedIcon />}
              label="Branch Name"
              required
              placeholder="e.g. Head Office"
              value={form.name}
              onChange={(e) => onChange("name", e.target.value.slice(0, 25))}
              error={errorField === "name"}
              helperText={
                errorField === "name"
                  ? "Branch name is required"
                  : "Max 25 characters"
              }
            />
          </Cell>

          <Cell span={{ xs: 6, sm: 3 }}>
            <IconField
              icon={<BadgeRoundedIcon />}
              label="Branch Code"
              required
              placeholder="ABC"
              value={form.code}
              onChange={(e) => onChange("code", sanitizeCode(e.target.value))}
              error={errorField === "code"}
              helperText={
                errorField === "code"
                  ? "Branch code is required"
                  : "Unique across all stores"
              }
            />
          </Cell>

          <Cell span={{ xs: 6, sm: 4 }}>
            <IconField
              icon={<CurrencyRupeeRoundedIcon />}
              label="Withstanding Amount"
              placeholder="Optional"
              inputMode="decimal"
              value={form.amount}
              onChange={(e) =>
                onChange("amount", sanitizeAmount(e.target.value))
              }
              helperText="Blank means not set"
            />
          </Cell>

          <Cell span={{ xs: 12, sm: 7 }}>
            <IconField
              icon={<PlaceRoundedIcon />}
              label="Address"
              placeholder="Street, city, pincode"
              value={form.address}
              onChange={(e) =>
                onChange("address", e.target.value.slice(0, 100))
              }
              helperText="Max 100 characters"
            />
          </Cell>

          <Cell span={{ xs: 12, sm: 5 }}>
            <IconField
              icon={<LinkRoundedIcon />}
              label="Location URL"
              placeholder="Google Maps link"
              value={form.location_url}
              onChange={(e) => onChange("location_url", e.target.value)}
              helperText="Optional"
            />
          </Cell>
        </FieldGrid>
      </SubSection>

      <SubSection label="Network & Remote Access" hint="support tools" divider>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
          <FieldGrid>
            <Cell span={{ xs: 12, sm: 6 }}>
              <IconField
                icon={<StorageRoundedIcon />}
                label="Server IP"
                placeholder="192.168.0.1"
                value={form.server_ip}
                onChange={(e) => onChange("server_ip", e.target.value)}
              />
            </Cell>
            <Cell span={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  pt: { xs: 0, sm: 0.5 },
                }}
              >
                <Segmented
                  options={[
                    { value: "anydesk", label: "AnyDesk" },
                    { value: "ultraviewer", label: "UltraViewer" },
                  ]}
                  value={remoteTool}
                  onChange={(next) => {
                    setRemoteTool(next);
                    onChange("anydesk_id", "");
                    onChange("ultraview_id", "");
                    onChange("ultraview_password", "");
                  }}
                />
              </Box>
            </Cell>
          </FieldGrid>

          {remoteTool === "" && (
            <Typography sx={{ fontSize: "0.72rem", color: INK_MUTED }}>
              Pick a remote tool to store this branch's connection details.
            </Typography>
          )}

          {remoteTool === "anydesk" && (
            <FieldGrid>
              <Cell span={{ xs: 12, sm: 6 }}>
                <IconField
                  icon={<DesktopWindowsRoundedIcon />}
                  label="AnyDesk ID"
                  placeholder="Enter AnyDesk ID"
                  value={form.anydesk_id}
                  onChange={(e) => onChange("anydesk_id", e.target.value)}
                />
              </Cell>
            </FieldGrid>
          )}

          {remoteTool === "ultraviewer" && (
            <FieldGrid>
              <Cell span={{ xs: 12, sm: 6 }}>
                <IconField
                  icon={<DesktopWindowsRoundedIcon />}
                  label="UltraViewer ID"
                  placeholder="Enter UltraViewer ID"
                  value={form.ultraview_id}
                  onChange={(e) => onChange("ultraview_id", e.target.value)}
                />
              </Cell>
              <Cell span={{ xs: 12, sm: 6 }}>
                <IconField
                  icon={<DesktopWindowsRoundedIcon />}
                  label="UltraViewer Password"
                  placeholder="Enter password"
                  value={form.ultraview_password}
                  onChange={(e) =>
                    onChange("ultraview_password", e.target.value)
                  }
                />
              </Cell>
            </FieldGrid>
          )}
        </Box>
      </SubSection>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// One row in the branch list
// ─────────────────────────────────────────────────────────────────────────────

const MetaBit = ({ icon, children }) =>
  children ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.4,
        minWidth: 0,
        "& svg": { fontSize: 13, color: INK_MUTED },
      }}
    >
      {icon}
      <Typography
        noWrap
        sx={{ fontSize: "0.7rem", color: INK_MUTED, minWidth: 0 }}
      >
        {children}
      </Typography>
    </Box>
  ) : null;

const BranchRow = ({
  branch,
  onlyBranch,
  busy,
  onEdit,
  onDelete,
  onSetMain,
}) => {
  const amount = formatAmount(branch.amount);
  const remote = branch.anydesk_id
    ? `AnyDesk- ${branch.anydesk_id}`
    : branch.ultraview_id
    ? `UltraViewer- ${branch.ultraview_id}`
    : null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        borderRadius: 2.5,
        border: `1px solid ${branch.is_main ? "#BFDBFE" : BORDER}`,
        // The main branch is the one the store answers as, so it reads as the
        // headline of the list rather than just another row.
        bgcolor: branch.is_main ? BLUE_PALE : SURFACE,
        borderLeft: `3px solid ${branch.is_main ? BLUE : BORDER}`,
        transition: "all 0.18s ease",
        "&:hover": { borderColor: "#BFDBFE" },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
          }}
        >
          {/* Star and name are one item so a narrow screen can never wrap the
              star onto a line of its own above the branch it marks. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            {branch.is_main && (
              <StarRoundedIcon
                sx={{ fontSize: 15, color: BLUE_DARK, flexShrink: 0 }}
              />
            )}
            <Typography
              sx={{
                fontSize: "0.83rem",
                fontWeight: 700,
                color: INK,
                minWidth: 0,
              }}
            >
              {branch.name}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              fontFamily: "monospace",
              color: BLUE_DARK,
              bgcolor: branch.is_main ? "#DBEAFE" : BLUE_PALE,
              border: "1px solid #BFDBFE",
              borderRadius: 1,
              px: 0.6,
              py: 0.1,
            }}
          >
            {branch.code}
          </Typography>
          {branch.is_main && (
            <Typography
              sx={{
                fontSize: "0.6rem",
                fontWeight: 800,
                letterSpacing: "0.5px",
                color: "#fff",
                bgcolor: BLUE_DARK,
                borderRadius: 1,
                px: 0.6,
                py: 0.15,
              }}
            >
              MAIN
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexWrap: "wrap",
            mt: 0.4,
          }}
        >
          <MetaBit icon={<PlaceRoundedIcon />}>{branch.address}</MetaBit>
          <MetaBit icon={<StorageRoundedIcon />}>{branch.server_ip}</MetaBit>
          <MetaBit icon={<DesktopWindowsRoundedIcon />}>{remote}</MetaBit>

          {branch.location_url && (
            <Tooltip title="View in google maps" placement="top">
              <Box
                component="a"
                href={branch.location_url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.3,
                  textDecoration: "none",
                  color: BLUE_DARK,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <LinkRoundedIcon sx={{ fontSize: 13 }} />
                Location
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Amount Pending */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.6,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.68rem",
              fontWeight: 600,
              color: INK_SOFT,
            }}
          >
            Amount Pending:
          </Typography>

          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 700,
              color: amount > 0 ? DANGER : INK,
            }}
          >
            ₹{amount}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}
      >
        {!branch.is_main && (
          <Button
            onClick={onSetMain}
            disabled={busy}
            size="small"
            startIcon={<StarRoundedIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: "none",
              fontSize: "0.68rem",
              fontWeight: 700,
              color: BLUE_DARK,
              borderRadius: 1.5,
              px: 1,
              py: 0.2,
              minWidth: 0,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: BLUE_PALE },
            }}
          >
            Set as main
          </Button>
        )}

        <Tooltip title="Edit branch" arrow>
          <span>
            <IconButton
              onClick={onEdit}
              disabled={busy}
              size="small"
              sx={{ color: INK_SOFT, "&:hover": { bgcolor: BLUE_PALE } }}
            >
              <EditRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip
          title={
            onlyBranch
              ? "A store must keep at least one branch"
              : branch.is_main
              ? "Set another branch as main first"
              : "Delete branch"
          }
          arrow
        >
          <span>
            <IconButton
              onClick={onDelete}
              disabled={busy || onlyBranch || branch.is_main}
              size="small"
              sx={{ color: DANGER, "&:hover": { bgcolor: "#FEF2F2" } }}
            >
              <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Add / edit dialog
// ─────────────────────────────────────────────────────────────────────────────

const BranchDialog = ({
  open,
  form,
  setForm,
  isEdit,
  canChooseMain,
  saving,
  onClose,
  onSave,
  errorField,
}) => {
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: fullScreen ? 0 : 3, overflow: "hidden" } },
        backdrop: { sx: { backdropFilter: "blur(3px)" } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          color: "#fff",
          background: `linear-gradient(100deg, ${INK} 0%, ${BLUE_DARK} 100%)`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.14)",
              border: "1px solid rgba(255,255,255,0.22)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HomeWorkRoundedIcon sx={{ fontSize: 17, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.92rem", fontWeight: 700 }}>
              {isEdit ? "Edit Branch" : "Add Branch"}
            </Typography>
            <Typography
              sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.75)" }}
            >
              {isEdit
                ? form.name || "Update this branch's details"
                : "Adding a new branch"}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={saving}
          size="small"
          sx={{ color: "#fff" }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: SURFACE }}>
        <BranchFields
          form={form}
          onChange={handleChange}
          errorField={errorField}
        />

        {canChooseMain && (
          <Box sx={{ mt: 2 }}>
            <ToggleRow
              title="Make this the main branch"
              caption={
                form.is_main && isEdit
                  ? "Already the main branch — promote another branch to move it"
                  : "The store answers as this branch: its code numbers tickets and its address is the store's address"
              }
              checked={Boolean(form.is_main)}
              disabled={isEdit && Boolean(form.is_main)}
              onChange={(e) => handleChange("is_main", e.target.checked)}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          borderTop: `1px solid ${BORDER}`,
          bgcolor: BG_ALT,
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            textTransform: "none",
            fontSize: "0.82rem",
            fontWeight: 600,
            color: INK_MUTED,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          disabled={saving}
          startIcon={
            saving ? (
              <CircularProgress size={14} sx={{ color: "#fff" }} />
            ) : (
              <SaveRoundedIcon />
            )
          }
          sx={{
            textTransform: "none",
            fontSize: "0.82rem",
            fontWeight: 700,
            color: "#fff",
            px: 2.5,
            py: 0.75,
            borderRadius: 2,
            minWidth: 150,
            background: `linear-gradient(100deg, ${BLUE} 0%, ${BLUE_DARK} 100%)`,
            "&:hover": {
              background: `linear-gradient(100deg, ${BLUE_DARK} 0%, #1E3A8A 100%)`,
            },
            "&.Mui-disabled": { color: "#fff", opacity: 0.7 },
          }}
        >
          {isEdit ? "Save Branch" : "Add Branch"}
        </Button>
      </Box>
    </Dialog>
  );
};

const ConfirmDeleteDialog = ({ branch, busy, onCancel, onConfirm }) => (
  <Dialog
    open={Boolean(branch)}
    onClose={busy ? undefined : onCancel}
    slotProps={{
      paper: { sx: { borderRadius: 4, width: "90%", maxWidth: 360, p: 3 } },
      backdrop: { sx: { backdropFilter: "blur(4px)" } },
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 58,
          height: 58,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#FEF2F2",
          border: "1px solid #FECACA",
        }}
      >
        <WarningAmberRoundedIcon sx={{ fontSize: 28, color: DANGER }} />
      </Box>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: INK }}>
        Delete this branch?
      </Typography>
      <Typography sx={{ fontSize: "0.8rem", color: INK_MUTED }}>
        {branch?.name} ({branch?.code}) will be removed from this store. Its
        address, amount and remote-access details go with it.
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
        <Button
          onClick={onCancel}
          disabled={busy}
          sx={{
            textTransform: "none",
            fontSize: "0.82rem",
            fontWeight: 600,
            color: INK_MUTED,
            minWidth: 110,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={busy}
          startIcon={
            busy ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null
          }
          sx={{
            textTransform: "none",
            fontSize: "0.82rem",
            fontWeight: 700,
            color: "#fff",
            bgcolor: DANGER,
            borderRadius: 2,
            minWidth: 110,
            "&:hover": { bgcolor: "#B91C1C" },
            "&.Mui-disabled": { color: "#fff", bgcolor: DANGER, opacity: 0.7 },
          }}
        >
          Delete
        </Button>
      </Box>
    </Box>
  </Dialog>
);

// ─────────────────────────────────────────────────────────────────────────────
// The panel
// ─────────────────────────────────────────────────────────────────────────────

export const BranchPanel = ({
  storeId,
  initialBranches = [],
  onNotify,
  onChanged,
}) => {
  // Seeded from the row the directory navigated with, so the list is on screen
  // before the fetch comes back instead of flashing a spinner.
  const [branches, setBranches] = useState(initialBranches);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // { form, isEdit }
  const [errorField, setErrorField] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  // Held in a ref, not read straight from props: the parent passes an inline
  // arrow, so depending on it would give fetchBranches a new identity every
  // render and the load effect below would re-run itself forever.
  const notifyRef = useRef(onNotify);
  notifyRef.current = onNotify;

  const notify = useCallback(
    (message, severity = "success") => notifyRef.current?.(message, severity),
    [],
  );

  const fetchBranches = useCallback(async () => {
    if (!storeId) return [];
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/auth/stores/${storeId}/branches`,
      );
      if (!res?.data?.success) throw res?.data;
      const list = res.data.data || [];
      setBranches(list);
      return list;
    } catch (error) {
      notify(apiMessage(error, "Failed to load branches"), "error");
      return [];
    } finally {
      setLoading(false);
    }
  }, [storeId, notify]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const openAdd = () => {
    setErrorField(null);
    // A store with no branches yet has nothing to choose between, so the first
    // one is main whatever the toggle says — it is shown pre-set and locked.
    setDialog({
      form: { ...emptyBranch(), is_main: branches.length === 0 },
      isEdit: false,
    });
  };

  const openEdit = (branch) => {
    setErrorField(null);
    setDialog({ form: branchToForm(branch), isEdit: true });
  };

  const setForm = (updater) =>
    setDialog((prev) =>
      prev
        ? {
            ...prev,
            form: typeof updater === "function" ? updater(prev.form) : updater,
          }
        : prev,
    );

  const saveBranch = async () => {
    if (!dialog) return;
    const invalid = validateBranch(dialog.form);
    if (invalid) {
      setErrorField(invalid.field);
      notify(invalid.message, "error");
      return;
    }
    setErrorField(null);
    setBusy(true);
    try {
      const payload = {
        ...branchToPayload(dialog.form),
        ...(dialog.form.is_main ? { is_main: true } : {}),
      };
      const res = dialog.isEdit
        ? await axios.put(
            `${API_BASE_URL}/api/auth/branches/${dialog.form.id}`,
            payload,
          )
        : await axios.post(
            `${API_BASE_URL}/api/auth/stores/${storeId}/branches`,
            payload,
          );
      if (!res?.data?.success) throw res?.data;

      setDialog(null);
      const list = await fetchBranches();
      onChanged?.(list);
      notify(dialog.isEdit ? "Branch updated" : "Branch added", "success");
    } catch (error) {
      notify(apiMessage(error, "Failed to save branch"), "error");
    } finally {
      setBusy(false);
    }
  };

  const setMain = async (branch) => {
    setBusy(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/api/auth/branches/${branch.id}/main`,
      );
      if (!res?.data?.success) throw res?.data;
      const list = res.data.data || [];
      setBranches(list);
      onChanged?.(list);
      notify(res.data.statusMessage || "Main branch updated", "success");
    } catch (error) {
      notify(apiMessage(error, "Failed to set the main branch"), "error");
    } finally {
      setBusy(false);
    }
  };

  const deleteBranch = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/api/auth/branches/${pendingDelete.id}`,
      );
      if (!res?.data?.success) throw res?.data;
      const list = res.data.data || [];
      setBranches(list);
      setPendingDelete(null);
      onChanged?.(list);
      notify("Branch deleted", "success");
    } catch (error) {
      notify(apiMessage(error, "Failed to delete branch"), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: "0.72rem", color: INK_MUTED }}>
            {branches.length === 0
              ? "This store has no branches yet."
              : branches.length === 1
              ? "One branch — it is the main branch."
              : `${branches.length} branches · the starred one is the main branch`}
          </Typography>

          <Button
            onClick={openAdd}
            disabled={busy || !storeId}
            startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 700,
              color: BLUE_DARK,
              bgcolor: BLUE_PALE,
              border: "1px solid #BFDBFE",
              borderRadius: 2,
              px: 1.5,
              py: 0.4,
              "&:hover": { bgcolor: "#DBEAFE" },
            }}
          >
            Add Branch
          </Button>
        </Box>

        {loading && branches.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 3,
              borderRadius: 2.5,
              border: `1px dashed ${BORDER}`,
              bgcolor: BG_ALT,
            }}
          >
            <CircularProgress size={16} sx={{ color: BLUE }} />
            <Typography sx={{ fontSize: "0.76rem", color: INK_MUTED }}>
              Loading branches…
            </Typography>
          </Box>
        ) : branches.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              py: 3,
              borderRadius: 2.5,
              border: `1px dashed ${BORDER}`,
              bgcolor: BG_ALT,
            }}
          >
            <HomeWorkRoundedIcon sx={{ fontSize: 26, color: "#94A3B8" }} />
            <Typography
              sx={{ fontSize: "0.8rem", fontWeight: 700, color: INK_SOFT }}
            >
              No branches yet
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: INK_MUTED }}>
              Add one — the first branch becomes the main branch.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {branches.map((branch) => (
              <BranchRow
                key={branch.id}
                branch={branch}
                onlyBranch={branches.length === 1}
                busy={busy}
                onEdit={() => openEdit(branch)}
                onDelete={() => setPendingDelete(branch)}
                onSetMain={() => setMain(branch)}
              />
            ))}
          </Box>
        )}
      </Box>

      {dialog && (
        <BranchDialog
          open
          form={dialog.form}
          setForm={setForm}
          isEdit={dialog.isEdit}
          // Nothing to choose between when this is the store's first branch.
          canChooseMain={branches.length > 0}
          saving={busy}
          errorField={errorField}
          onClose={() => setDialog(null)}
          onSave={saveBranch}
        />
      )}

      <ConfirmDeleteDialog
        branch={pendingDelete}
        busy={busy}
        onCancel={() => setPendingDelete(null)}
        onConfirm={deleteBranch}
      />
    </>
  );
};

export default BranchPanel;
